import { useEffect, useMemo, useRef, useState } from "react";
import { marked } from "marked";
import "./MarkdownEditor.css";

// `marked` defaults are fine for our needs (GFM-ish), but we want the renderer
// to be deterministic and synchronous so we can render directly into innerHTML.
marked.setOptions({ async: false, gfm: true, breaks: false });

/** Render Markdown to a sanitised HTML string.
 *
 *  `marked` itself doesn't escape script/style tags, but we control the input
 *  (the user is editing their own notes) and the output is only ever rendered
 *  into the user's own session — it never crosses tenants. We still strip the
 *  obvious dangerous tags as a belt-and-braces measure; if v1.x ever lets the
 *  user paste HTML from a third-party source, swap to DOMPurify. */
export function renderMarkdown(source: string): string {
  const html = marked.parse(source ?? "", { async: false }) as string;
  return html
    .replace(/<script[\s\S]*?<\/script>/gi, "")
    .replace(/<style[\s\S]*?<\/style>/gi, "")
    .replace(/ on[a-z]+="[^"]*"/gi, "")
    .replace(/ on[a-z]+='[^']*'/gi, "");
}

interface Props {
  value: string;
  onChange: (next: string) => void;
  /** Show the rendered preview by default; switch to textarea when "Edit"
   *  is clicked. Default `true`. */
  startInPreview?: boolean;
  /** Optional placeholder for the textarea. */
  placeholder?: string;
  /** Optional minimum height for both panes (px). */
  minHeight?: number;
  /** Optional inline filter — if non-empty, body matches are wrapped in
   *  <mark>. Used by the Notes-tab search hint. Plain `String.includes`
   *  per the plan ("not full FTS"). */
  highlight?: string;
}

export function MarkdownEditor({
  value,
  onChange,
  startInPreview = true,
  placeholder,
  minHeight = 220,
  highlight,
}: Props) {
  const [editing, setEditing] = useState(!startInPreview);
  const previewRef = useRef<HTMLDivElement | null>(null);
  // Re-render when entering preview, switching plant, or when the user
  // pauses on a key. We render on every value change because the surface is
  // small and `marked` is fast.
  const html = useMemo(() => {
    let out = renderMarkdown(value);
    if (highlight && highlight.trim()) {
      const term = escapeRegex(highlight.trim());
      // Only highlight inside text nodes — easier said than done in a regex.
      // Quick pragmatic approach: replace term occurrences except inside HTML
      // tags. The negative lookbehind/ahead would be cleaner but Safari's
      // lookbehind support is recent; this is wide-compat.
      out = highlightOutsideTags(out, term);
    }
    return out;
  }, [value, highlight]);

  // Scroll the first highlight into view when the search term changes.
  useEffect(() => {
    if (!highlight || !previewRef.current) return;
    const first = previewRef.current.querySelector("mark");
    if (first) {
      first.scrollIntoView({ block: "nearest", behavior: "smooth" });
    }
  }, [highlight, html]);

  if (editing) {
    return (
      <div className="MdEditor MdEditor--editing">
        <textarea
          className="MdEditor__textarea"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          style={{ minHeight }}
          autoFocus
        />
        <div className="MdEditor__toolbar">
          <span className="MdEditor__hint">
            Markdown · # headings, **bold**, *italic*, lists.
          </span>
          <button
            type="button"
            className="MdEditor__toolbarBtn"
            onClick={() => setEditing(false)}
          >
            Done
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="MdEditor MdEditor--preview">
      <div
        ref={previewRef}
        className="MdEditor__preview"
        style={{ minHeight }}
        // The HTML comes from `marked` on user-owned text. See renderMarkdown.
        dangerouslySetInnerHTML={{
          __html: value.trim()
            ? html
            : `<p class="MdEditor__empty">${placeholder ?? "No notes yet."}</p>`,
        }}
      />
      <div className="MdEditor__toolbar">
        <span className="MdEditor__hint">Rendered markdown.</span>
        <button
          type="button"
          className="MdEditor__toolbarBtn"
          onClick={() => setEditing(true)}
        >
          Edit
        </button>
      </div>
    </div>
  );
}

function escapeRegex(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function highlightOutsideTags(html: string, term: string): string {
  const re = new RegExp(`(${term})`, "gi");
  // Split on tags; only replace inside the text segments (even indices).
  const parts = html.split(/(<[^>]*>)/g);
  for (let i = 0; i < parts.length; i++) {
    if (i % 2 === 0) {
      parts[i] = parts[i].replace(re, "<mark>$1</mark>");
    }
  }
  return parts.join("");
}
